import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Building2, User, FileText, SlidersHorizontal, Landmark, ShieldCheck, MessageSquare, ScrollText, FlaskConical, ReceiptText, Download } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { PageHeader, Loading, ErrorState } from "@/components/Bits";
import { Field, inputCls, primaryBtnCls, ghostBtnCls } from "@/components/Kit";
import TemplatesManager from "@/components/TemplatesManager";
import PharmacyIdentity from "@/pages/pharmacy/PharmacyIdentity";
import { useAuth } from "@/context/AuthContext";
import { inr, BUSINESS_TYPE_LABEL } from "@/lib/format";

const TABS = [
  ["business", "Business Profile", Building2],
  ["profile", "My Profile", User],
  ["document", "Document Settings", FileText],
  ["documents", "Documents & Templates", ScrollText],
  ["lab_templates", "Lab Report Templates", FlaskConical],
  ["pharmacy_templates", "Pharmacy Documents", ReceiptText],
  ["pharmacy_identity", "Pharmacy Identity", Building2],
  ["general", "General Settings", SlidersHorizontal],
  ["bank", "Bank Details", Landmark],
  ["notifications", "WhatsApp & Notifications", MessageSquare],
  ["security", "Security", ShieldCheck],
];

const TEMPLATES = [
  ["classic", "Classic", "Traditional ruled tax invoice with a bordered header."],
  ["modern", "Modern", "Clean type-led layout with a coloured accent band."],
  ["minimal", "Minimal", "Whitespace-first layout, ideal for receipts."],
  ["healthcare", "Professional Healthcare", "Clinical layout with patient block and vitals space."],
  ["compact", "Compact Pharmacy", "Dense line items for long medicine bills."],
  ["premium", "Premium", "Serif headings with a signature and seal area."],
];

export default function SettingsPage() {
  const { user, refresh, can } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("business");
  const [biz, setBiz] = useState({});
  const [prof, setProf] = useState({});
  const [doc, setDoc] = useState({});
  const [gen, setGen] = useState({});
  const [bank, setBank] = useState({});
  const [pwd, setPwd] = useState({ current_password: "", new_password: "", confirm: "" });
  const [emailChange, setEmailChange] = useState({ new_email: "", otp: "", sent: false });

  const load = useCallback(async () => {
    try {
      const { data: res } = await api.get("/settings/full");
      setData(res);
      const b = res.business || {};
      setBiz({ name: b.name || "", gstin: b.gstin || "", pan: b.pan || "", phone: b.phone || "",
        email: b.email || "", alternate_email: b.alternate_email || "", address: b.address || "",
        city: b.city || "", state: b.state || "", pincode: b.pincode || "", website: b.website || "",
        registration_no: b.registration_no || "", drug_licence: b.drug_licence || "",
        drug_licence_expiry: b.drug_licence_expiry || "",
        expiry_warning_days: String(b.expiry_warning_days ?? 90), logo_data: b.logo_data || "" });
      setDoc({ template: b.document?.template || "healthcare", font_family: b.document?.font_family || "Inter",
        font_size: String(b.document?.font_size || 12), accent_color: b.document?.accent_color || "#EA580C",
        logo_placement: b.document?.logo_placement || "left",
        show_business_details: b.document?.show_business_details ?? true,
        show_bank_details: b.document?.show_bank_details ?? false,
        footer_text: b.document?.footer_text || "", terms: b.document?.terms || "",
        signature_label: b.document?.signature_label || "Authorised signatory" });
      setGen({ currency: b.general?.currency || "INR", date_format: b.general?.date_format || "DD/MM/YYYY",
        time_format: b.general?.time_format || "12h", timezone: b.general?.timezone || "Asia/Kolkata",
        default_payment_method: b.general?.default_payment_method || "cash",
        invoice_prefix: b.general?.invoice_prefix || "INV", receipt_prefix: b.general?.receipt_prefix || "RCPT",
        prescription_prefix: b.general?.prescription_prefix || "RX",
        invoice_terms: b.general?.invoice_terms || "",
        opd_prescription_mode: b.general?.opd_prescription_mode || "system" });
      setBank(b.bank || { bank_name: "", account_holder: "", account_number: "", ifsc: "",
        branch: "", upi_id: "", show_on_invoice: false });
      setProf({ name: res.profile.name || "", phone: res.profile.phone || "", photo: res.profile.photo || "" });
    } catch (e) { setError(apiError(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);
  if (error) return <ErrorState message={error} />;
  if (!data) return <Loading label="Loading settings…" />;

  const canManage = data.can_manage;
  const isPharmacy = (data.business?.modules || []).includes("pharmacy");
  const isHospital = (data.business?.modules || []).includes("hospital");
  const isLab = (data.business?.modules || []).includes("lab");
  const save = async (fn, label) => {
    try { await fn(); toast.success(`${label} saved.`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const downloadTenantBackup = async () => {
    try {
      const response = await api.get(
        `/owner/businesses/${data.business.business_id}/backup`,
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(response.data);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `${data.business.name || "business"}-backup.zip`;
      anchor.click();
      URL.revokeObjectURL(url);
      toast.success("Tenant backup downloaded.");
    } catch (requestError) {
      toast.error(apiError(requestError));
    }
  };

  const readFile = (file, cb, maxKb = 900) => {
    if (!file) return;
    if (!/^image\/(png|jpe?g|webp)$/.test(file.type)) return toast.error("Please upload a PNG, JPG or WEBP image.");
    if (file.size > maxKb * 1024) return toast.error(`Image must be under ${maxKb} KB.`);
    const r = new FileReader();
    r.onload = () => cb(r.result);
    r.readAsDataURL(file);
  };

  const visibleTabs = TABS.filter(([k]) => {
    if (k === "documents") return isHospital && can("hospital.prescriptions.manage");
    if (k === "lab_templates") return isLab && can("lab.templates.manage");
    if (k === "pharmacy_identity") return isPharmacy && can("settings.manage");
    if (k === "pharmacy_templates") return isPharmacy && can("settings.manage");
    if (k === "notifications") return ["hospital", "lab", "pharmacy"].some((m) => can(`${m}.whatsapp.manage`));
    return k === "profile" || k === "security" || k === "notifications" || canManage || (k === "bank" && data.can_view_bank);
  });

  return (
    <div data-testid="settings-page">
      <PageHeader overline="Settings" title="Workspace settings"
        subtitle={`${data.business?.name} · ${BUSINESS_TYPE_LABEL[data.business?.business_type] || ""} · Business ID ${data.business?.code}`} />

      <div className="flex flex-wrap gap-2 mb-8">
        {visibleTabs.map(([key, label, Icon]) => (
          <button key={key} onClick={() => setTab(key)} data-testid={`settings-tab-${key}`}
            className={`inline-flex items-center gap-2 rounded-full px-5 py-2 text-sm font-medium border ${
              tab === key ? "bg-[#0F172A] text-white border-[#0F172A]" : "border-slate-300 text-slate-700 hover:border-slate-900"}`}>
            <Icon className="h-4 w-4" /> {label}
          </button>
        ))}
      </div>

      {tab === "business" && canManage && (
        <div className="space-y-6">
          <form onSubmit={(e) => { e.preventDefault();
            save(() => api.put("/settings/business", { ...biz, expiry_warning_days: Number(biz.expiry_warning_days) }), "Business profile"); }}
            className="panel p-6 md:p-8 grid gap-6 sm:grid-cols-3" data-testid="business-settings-form">
          <Field label="Business name"><input className={inputCls} data-testid="settings-name-input" value={biz.name} onChange={(e) => setBiz({ ...biz, name: e.target.value })} /></Field>
          <Field label="Business type"><input className={inputCls} disabled value={BUSINESS_TYPE_LABEL[data.business?.business_type] || ""} /></Field>
          <Field label="Business logo">
            <div className="flex items-center gap-3">
              {biz.logo_data && <img src={biz.logo_data} alt="Business logo" className="h-10 w-10 rounded border border-slate-200 object-contain" />}
              <input type="file" accept="image/*" data-testid="settings-logo-input"
                onChange={(e) => readFile(e.target.files[0], (d) => setBiz({ ...biz, logo_data: d }))}
                className="text-sm text-slate-700" />
              {biz.logo_data && <button type="button" className={ghostBtnCls} onClick={() => setBiz({ ...biz, logo_data: "" })}>Remove</button>}
            </div>
          </Field>
          <Field label="GSTIN"><input className={inputCls} data-testid="settings-gstin-input" value={biz.gstin} onChange={(e) => setBiz({ ...biz, gstin: e.target.value.toUpperCase() })} /></Field>
          <Field label="Business PAN"><input className={inputCls} value={biz.pan} onChange={(e) => setBiz({ ...biz, pan: e.target.value.toUpperCase() })} /></Field>
          <Field label="Registration / licence no."><input className={inputCls} value={biz.registration_no} onChange={(e) => setBiz({ ...biz, registration_no: e.target.value })} /></Field>
          <Field label="Contact number"><input className={inputCls} value={biz.phone} onChange={(e) => setBiz({ ...biz, phone: e.target.value })} /></Field>
          <Field label="Business email"><input className={inputCls} value={biz.email} onChange={(e) => setBiz({ ...biz, email: e.target.value })} /></Field>
          <Field label="Alternate email"><input className={inputCls} value={biz.alternate_email} onChange={(e) => setBiz({ ...biz, alternate_email: e.target.value })} /></Field>
          <Field label="City"><input className={inputCls} value={biz.city} onChange={(e) => setBiz({ ...biz, city: e.target.value })} /></Field>
          <Field label="State"><input className={inputCls} value={biz.state} onChange={(e) => setBiz({ ...biz, state: e.target.value })} /></Field>
          <Field label="PIN code"><input className={inputCls} value={biz.pincode} onChange={(e) => setBiz({ ...biz, pincode: e.target.value })} /></Field>
          <Field label="Website"><input className={inputCls} value={biz.website} onChange={(e) => setBiz({ ...biz, website: e.target.value })} /></Field>
          <Field label="Full address" className="sm:col-span-2"><input className={inputCls} value={biz.address} onChange={(e) => setBiz({ ...biz, address: e.target.value })} /></Field>
          {isPharmacy && (
            <>
              <Field label="Drug licence number"><input className={inputCls} data-testid="settings-drug-licence-input" value={biz.drug_licence} onChange={(e) => setBiz({ ...biz, drug_licence: e.target.value })} /></Field>
              <Field label="Drug licence valid till"><input type="date" className={inputCls} value={biz.drug_licence_expiry} onChange={(e) => setBiz({ ...biz, drug_licence_expiry: e.target.value })} /></Field>
              <Field label="Expiry warning window">
                <select className={inputCls} value={biz.expiry_warning_days} data-testid="expiry-window-select"
                  onChange={(e) => setBiz({ ...biz, expiry_warning_days: e.target.value })}>
                  {["30", "60", "90", "120", "180"].map((d) => <option key={d} value={d} label={`${d} days`} />)}
                </select>
              </Field>
            </>
          )}
            <div className="sm:col-span-3"><button className={primaryBtnCls} data-testid="save-business-settings-btn">Save business profile</button></div>
          </form>
          {user?.role === "business_admin" && (
            <section className="panel flex flex-wrap items-center justify-between gap-5 p-6" data-testid="tenant-backup-panel">
              <div>
                <p className="font-ui text-lg font-bold text-slate-900">Tenant backup</p>
                <p className="mt-1 max-w-2xl text-sm text-slate-600">
                  Download this business’s data and supported document/template files. Credentials,
                  sessions, tokens, and other secrets are excluded.
                </p>
              </div>
              <button className={ghostBtnCls} type="button" data-testid="tenant-backup-download-btn" onClick={downloadTenantBackup}>
                <Download className="h-4 w-4" /> Download backup
              </button>
            </section>
          )}
        </div>
      )}

      {tab === "profile" && (
        <div className="grid gap-6 lg:grid-cols-2">
          <form onSubmit={(e) => { e.preventDefault(); save(async () => { await api.put("/me/profile", prof); await refresh(); }, "Profile"); }}
            className="panel p-6 md:p-8 space-y-5" data-testid="profile-form">
            <h3 className="font-ui text-lg font-bold text-slate-900">My profile</h3>
            <div className="flex items-center gap-4">
              {prof.photo ? <img src={prof.photo} alt="Profile" className="h-16 w-16 rounded-full object-cover border border-slate-200" />
                : <span className="grid h-16 w-16 place-items-center rounded-full bg-[#0F172A] text-white font-bold">
                    {String(user?.name || "U").slice(0, 2).toUpperCase()}</span>}
              <input type="file" accept="image/*" data-testid="profile-photo-input" className="text-sm text-slate-700"
                onChange={(e) => readFile(e.target.files[0], (d) => setProf({ ...prof, photo: d }))} />
              {prof.photo && <button type="button" className={ghostBtnCls} onClick={() => setProf({ ...prof, photo: "" })}>Remove</button>}
            </div>
            <Field label="Full name"><input className={inputCls} data-testid="profile-name-input" value={prof.name} onChange={(e) => setProf({ ...prof, name: e.target.value })} /></Field>
            <Field label="Phone"><input className={inputCls} value={prof.phone} onChange={(e) => setProf({ ...prof, phone: e.target.value })} /></Field>
            <Field label="Role (managed by your administrator)"><input className={inputCls} disabled value={String(data.profile.role || "").replace(/_/g, " ")} /></Field>
            <button className={primaryBtnCls} data-testid="save-profile-btn">Save profile</button>
          </form>

          <div className="space-y-6">
            <form onSubmit={(e) => { e.preventDefault();
              if (pwd.new_password !== pwd.confirm) return toast.error("Both new passwords must match.");
              save(async () => { await api.post("/me/password", { current_password: pwd.current_password, new_password: pwd.new_password }); setPwd({ current_password: "", new_password: "", confirm: "" }); }, "Password"); }}
              className="panel p-6 md:p-8 space-y-5" data-testid="password-form">
              <h3 className="font-ui text-lg font-bold text-slate-900">Change password</h3>
              <Field label="Current password"><input type="password" required className={inputCls} data-testid="current-password-input" value={pwd.current_password} onChange={(e) => setPwd({ ...pwd, current_password: e.target.value })} /></Field>
              <Field label="New password"><input type="password" required minLength={8} className={inputCls} data-testid="new-password-input" value={pwd.new_password} onChange={(e) => setPwd({ ...pwd, new_password: e.target.value })} /></Field>
              <Field label="Confirm new password"><input type="password" required className={inputCls} data-testid="confirm-password-input" value={pwd.confirm} onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })} /></Field>
              <button className={primaryBtnCls} data-testid="save-password-btn">Update password</button>
            </form>

            <div className="panel p-6 md:p-8 space-y-5">
              <h3 className="font-ui text-lg font-bold text-slate-900">Change sign-in email</h3>
              <p className="text-sm text-slate-600">Current email: <strong>{data.profile.email}</strong>. A code is sent to the new address; your sign-in email changes only after it is verified.</p>
              {!emailChange.sent ? (
                <form onSubmit={async (e) => { e.preventDefault();
                  try { const { data: r } = await api.post("/me/email-change", { new_email: emailChange.new_email });
                    toast.success(r.message); setEmailChange({ ...emailChange, sent: true }); }
                  catch (err) { toast.error(apiError(err)); } }} className="space-y-4">
                  <Field label="New email"><input type="email" required className={inputCls} data-testid="new-email-input" value={emailChange.new_email} onChange={(e) => setEmailChange({ ...emailChange, new_email: e.target.value })} /></Field>
                  <button className={primaryBtnCls} data-testid="send-email-code-btn">Send verification code</button>
                </form>
              ) : (
                <form onSubmit={async (e) => { e.preventDefault();
                  try { await api.post("/me/email-change/confirm", { otp: emailChange.otp });
                    toast.success("Email updated."); setEmailChange({ new_email: "", otp: "", sent: false }); await refresh(); load(); }
                  catch (err) { toast.error(apiError(err)); } }} className="space-y-4">
                  <Field label="Verification code"><input required className={inputCls} data-testid="email-otp-input" value={emailChange.otp} onChange={(e) => setEmailChange({ ...emailChange, otp: e.target.value })} /></Field>
                  <button className={primaryBtnCls} data-testid="confirm-email-btn">Confirm new email</button>
                </form>
              )}
            </div>
          </div>
        </div>
      )}

      {tab === "document" && canManage && (
        <div className="grid gap-6 lg:grid-cols-5">
          <form onSubmit={(e) => { e.preventDefault();
            save(() => api.put("/settings/document", { ...doc, font_size: Number(doc.font_size) }), "Document settings"); }}
            className="panel p-6 md:p-8 space-y-6 lg:col-span-3" data-testid="document-settings-form">
            <div>
              <p className="text-[12px] font-semibold uppercase tracking-wider text-slate-700 mb-3">Invoice template</p>
              <div className="grid gap-3 sm:grid-cols-2">
                {TEMPLATES.map(([key, label, desc]) => (
                  <button type="button" key={key} onClick={() => setDoc({ ...doc, template: key })}
                    data-testid={`template-${key}`}
                    className={`rounded-lg border p-4 text-left ${doc.template === key ? "border-[#0F172A] bg-slate-50" : "border-slate-200 hover:border-slate-400"}`}>
                    <p className="font-semibold text-slate-900 text-sm">{label}</p>
                    <p className="text-xs text-slate-600 mt-1">{desc}</p>
                  </button>
                ))}
              </div>
            </div>
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label="Font family">
                <select className={inputCls} value={doc.font_family} data-testid="doc-font-select" onChange={(e) => setDoc({ ...doc, font_family: e.target.value })}>
                  {["Inter", "Manrope", "IBM Plex Sans", "Georgia", "Times New Roman", "Arial", "Courier New"].map((f) => <option key={f} value={f}>{f}</option>)}
                </select>
              </Field>
              <Field label="Font size (pt)"><input type="number" min={9} max={16} className={inputCls} value={doc.font_size} onChange={(e) => setDoc({ ...doc, font_size: e.target.value })} /></Field>
              <Field label="Accent colour"><input type="color" className={`${inputCls} h-11 p-1`} data-testid="doc-color-input" value={doc.accent_color} onChange={(e) => setDoc({ ...doc, accent_color: e.target.value })} /></Field>
              <Field label="Logo placement">
                <select className={inputCls} value={doc.logo_placement} onChange={(e) => setDoc({ ...doc, logo_placement: e.target.value })}>
                  {["left", "center", "right", "hidden"].map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </Field>
              <Field label="Signature label"><input className={inputCls} value={doc.signature_label} onChange={(e) => setDoc({ ...doc, signature_label: e.target.value })} /></Field>
              <Field label="Footer text"><input className={inputCls} value={doc.footer_text} onChange={(e) => setDoc({ ...doc, footer_text: e.target.value })} /></Field>
              <Field label="Terms & conditions" className="sm:col-span-2"><textarea rows={3} className={inputCls} value={doc.terms} onChange={(e) => setDoc({ ...doc, terms: e.target.value })} /></Field>
            </div>
            <div className="flex flex-wrap gap-6 text-sm text-slate-700">
              <label className="flex items-center gap-2"><input type="checkbox" className="h-4 w-4 accent-[#0F172A]" checked={doc.show_business_details} onChange={(e) => setDoc({ ...doc, show_business_details: e.target.checked })} /> Show business details</label>
              <label className="flex items-center gap-2"><input type="checkbox" className="h-4 w-4 accent-[#0F172A]" checked={doc.show_bank_details} onChange={(e) => setDoc({ ...doc, show_bank_details: e.target.checked })} /> Show bank details</label>
            </div>
            <button className={primaryBtnCls} data-testid="save-document-settings-btn">Save document settings</button>
          </form>

          <div className="panel p-6 lg:col-span-2" data-testid="document-preview">
            <p className="text-[12px] font-semibold uppercase tracking-wider text-slate-700 mb-4">Live preview</p>
            <div className="rounded-lg border border-slate-200 p-5" style={{ fontFamily: doc.font_family, fontSize: `${doc.font_size}px` }}>
              <div style={{ borderBottom: `3px solid ${doc.accent_color}`, paddingBottom: 8,
                textAlign: doc.logo_placement === "center" ? "center" : doc.logo_placement === "right" ? "right" : "left" }}>
                {doc.logo_placement !== "hidden" && biz.logo_data && <img src={biz.logo_data} alt="logo" style={{ height: 32, display: "inline-block" }} />}
                <p style={{ fontWeight: 700, textTransform: "uppercase" }}>{biz.name || "Business name"}</p>
                {doc.show_business_details && <p style={{ color: "#475569" }}>{[biz.address, biz.city, biz.state].filter(Boolean).join(", ")}<br />{biz.phone} · GSTIN {biz.gstin || "—"}</p>}
              </div>
              <p style={{ marginTop: 10, fontWeight: 600 }}>Tax invoice · {gen.invoice_prefix}00042</p>
              <p style={{ color: "#475569" }}>Patient: Sample Patient · UHID UH00007</p>
              <table style={{ width: "100%", marginTop: 10, borderTop: "1px solid #e2e8f0" }}>
                <tbody>
                  <tr><td>Consultation</td><td style={{ textAlign: "right" }}>{inr(500)}</td></tr>
                  <tr><td>GST · module-specific rate</td><td style={{ textAlign: "right" }}>{inr(60)}</td></tr>
                  <tr style={{ fontWeight: 700 }}><td>Total</td><td style={{ textAlign: "right" }}>{inr(560)}</td></tr>
                </tbody>
              </table>
              {doc.show_bank_details && bank.bank_name && <p style={{ marginTop: 10, color: "#475569" }}>Bank: {bank.bank_name} · A/C {bank.account_number} · IFSC {bank.ifsc}</p>}
              {doc.terms && <p style={{ marginTop: 10, color: "#475569" }}>{doc.terms}</p>}
              <p style={{ marginTop: 24, textAlign: "right", color: "#475569" }}>{doc.signature_label}</p>
              {doc.footer_text && <p style={{ marginTop: 10, textAlign: "center", color: "#94a3b8" }}>{doc.footer_text}</p>}
            </div>
          </div>
        </div>
      )}

      {tab === "documents" && isHospital && can("hospital.prescriptions.manage") && (
        <div className="panel p-6" data-testid="settings-documents-panel">
          <TemplatesManager module="hospital" business={data.business} />
        </div>
      )}

      {tab === "lab_templates" && isLab && can("lab.templates.manage") && (
        <div className="panel p-6" data-testid="settings-lab-templates-panel">
          <TemplatesManager module="lab" />
        </div>
      )}

      {tab === "pharmacy_identity" && isPharmacy && canManage && (
        <div className="panel p-6" data-testid="settings-pharmacy-identity-panel">
          <PharmacyIdentity />
        </div>
      )}

      {tab === "pharmacy_templates" && isPharmacy && can("settings.manage") && (
        <div className="panel p-6" data-testid="settings-pharmacy-templates-panel">
          <TemplatesManager module="pharmacy" business={data.business} />
        </div>
      )}

      {tab === "general" && canManage && (
        <form onSubmit={(e) => { e.preventDefault();
          save(() => api.put("/settings/general", gen), "General settings"); }}
          className="panel p-6 md:p-8 grid gap-6 sm:grid-cols-3" data-testid="general-settings-form">
          <Field label="Currency"><select className={inputCls} value={gen.currency} onChange={(e) => setGen({ ...gen, currency: e.target.value })}><option value="INR">INR (₹)</option></select></Field>
          <Field label="Date format"><select className={inputCls} value={gen.date_format} data-testid="date-format-select" onChange={(e) => setGen({ ...gen, date_format: e.target.value })}>{["DD/MM/YYYY", "DD-MM-YYYY", "YYYY-MM-DD"].map((f) => <option key={f} value={f}>{f}</option>)}</select></Field>
          <Field label="Time format"><select className={inputCls} value={gen.time_format} onChange={(e) => setGen({ ...gen, time_format: e.target.value })}><option value="12h">12 hour</option><option value="24h">24 hour</option></select></Field>
          <Field label="Time zone"><select className={inputCls} value={gen.timezone} onChange={(e) => setGen({ ...gen, timezone: e.target.value })}><option value="Asia/Kolkata">Asia/Kolkata (IST)</option></select></Field>
          <Field label="Default payment method"><select className={inputCls} value={gen.default_payment_method} onChange={(e) => setGen({ ...gen, default_payment_method: e.target.value })}>{["cash", "card", "upi", "netbanking"].map((m) => <option key={m} value={m}>{m}</option>)}</select></Field>
          <Field label="Invoice number prefix"><input className={inputCls} data-testid="invoice-prefix-input" value={gen.invoice_prefix} onChange={(e) => setGen({ ...gen, invoice_prefix: e.target.value })} /></Field>
          <Field label="Receipt number prefix"><input className={inputCls} value={gen.receipt_prefix} onChange={(e) => setGen({ ...gen, receipt_prefix: e.target.value })} /></Field>
          <Field label="Prescription number prefix"><input className={inputCls} value={gen.prescription_prefix} onChange={(e) => setGen({ ...gen, prescription_prefix: e.target.value })} /></Field>
          <Field label="OPD prescription mode"><select className={inputCls} data-testid="opd-prescription-mode-select" value={gen.opd_prescription_mode} onChange={(e) => setGen({ ...gen, opd_prescription_mode: e.target.value })}><option value="system">System-generated prescription</option><option value="manual">Manual / uploaded prescription pad</option></select></Field>
          <Field label="Default invoice terms" className="sm:col-span-3"><textarea rows={2} className={inputCls} value={gen.invoice_terms} onChange={(e) => setGen({ ...gen, invoice_terms: e.target.value })} /></Field>
          <div className="sm:col-span-3"><button className={primaryBtnCls} data-testid="save-general-settings-btn">Save general settings</button></div>
        </form>
      )}

      {tab === "bank" && (
        data.can_view_bank ? (
          <form onSubmit={(e) => { e.preventDefault(); save(() => api.put("/settings/bank", bank), "Bank details"); }}
            className="panel p-6 md:p-8 grid gap-6 sm:grid-cols-3 max-w-4xl" data-testid="bank-settings-form">
            <Field label="Bank name"><input className={inputCls} data-testid="bank-name-input" value={bank.bank_name} onChange={(e) => setBank({ ...bank, bank_name: e.target.value })} /></Field>
            <Field label="Account holder"><input className={inputCls} value={bank.account_holder} onChange={(e) => setBank({ ...bank, account_holder: e.target.value })} /></Field>
            <Field label="Account number"><input className={inputCls} data-testid="bank-account-input" value={bank.account_number} onChange={(e) => setBank({ ...bank, account_number: e.target.value })} /></Field>
            <Field label="IFSC"><input className={inputCls} data-testid="bank-ifsc-input" value={bank.ifsc} onChange={(e) => setBank({ ...bank, ifsc: e.target.value.toUpperCase() })} /></Field>
            <Field label="Branch"><input className={inputCls} value={bank.branch} onChange={(e) => setBank({ ...bank, branch: e.target.value })} /></Field>
            <Field label="UPI ID"><input className={inputCls} value={bank.upi_id} onChange={(e) => setBank({ ...bank, upi_id: e.target.value })} /></Field>
            <label className="sm:col-span-3 flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" className="h-4 w-4 accent-[#0F172A]" checked={bank.show_on_invoice}
                onChange={(e) => setBank({ ...bank, show_on_invoice: e.target.checked })} /> Show these details on invoices and receipts
            </label>
            {canManage && <div className="sm:col-span-3"><button className={primaryBtnCls} data-testid="save-bank-btn">Save bank details</button></div>}
          </form>
        ) : (
          <div className="panel p-8 text-sm text-slate-600">You do not have permission to view bank details.</div>
        )
      )}

      {tab === "general" && canManage && (
        <ModuleGstSettings
          business={data.business}
          canManage={canManage}
          onSaved={load}
        />
      )}

      {tab === "notifications" && (
        <NotificationsTab canManage={canManage} />
      )}

      {tab === "security" && (
        <div className="panel p-6 md:p-8 space-y-4 max-w-3xl text-sm text-slate-700">
          <h3 className="font-ui text-lg font-bold text-slate-900">Security posture</h3>
          {[["Authentication", "Email + password (bcrypt) and Google sign-in for authorised accounts only."],
            ["Session", "HttpOnly, secure cookie. Sessions expire automatically."],
            ["Tenant isolation", "Your business identifier is resolved from your session on every request — never from the browser."],
            ["Permissions", "Role defaults plus individual toggles, enforced on the server for every action."],
            ["Audit", "Logins, permission changes, invoices, voids, returns, stock adjustments, exports and attendance edits are logged."],
            ["Secrets", "Email and WhatsApp credentials are stored server-side and never sent to the browser."]].map(([k, v]) => (
            <div key={k} className="rounded-lg border border-slate-200 p-4">
              <p className="font-semibold text-slate-900">{k}</p>
              <p className="mt-1 text-slate-600">{v}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function NotificationsTab({ canManage }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState("");
  const load = useCallback(async () => {
    try { const { data: d } = await api.get("/settings/notifications"); setData(d); }
    catch (e) { setError(apiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  if (error) return <ErrorState message={error} />;
  if (!data) return <Loading label="Loading notifications…" />;

  const toggle = async (ev) => {
    if (!ev.can_toggle) return;
    setSaving(ev.key);
    try {
      await api.put("/settings/notifications", { key: ev.key, enabled: !ev.enabled });
      await load();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(""); }
  };

  const wa = data.whatsapp || {};
  return (
    <div className="space-y-6" data-testid="notifications-settings">
      <div className={`panel p-5 ${wa.configured ? "border-emerald-200" : "border-amber-200"}`}>
        <div className="flex items-center justify-between">
          <div>
            <p className="overline text-[#EA580C]">Provider · Interakt</p>
            <p className="mt-2 text-lg font-semibold text-slate-900">
              WhatsApp is {wa.configured ? "connected" : "not yet connected"}
            </p>
            <p className="mt-1 text-sm text-slate-600">
              API URL: <code className="text-xs">{wa.api_url}</code>
              {" "}· API key: {wa.configured ? "configured" : "missing (set WHATSAPP_API_TOKEN)"}
              {" "}· Webhook secret: {wa.webhook_secret_set ? "configured" : "missing (set WHATSAPP_WEBHOOK_SECRET)"}
            </p>
          </div>
        </div>
        <div className="mt-4">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Approved templates</p>
          <div className="grid gap-2 sm:grid-cols-2 text-xs">
            {(wa.templates || []).map((t) => (
              <div key={t.name} className="rounded-lg border border-slate-200 p-3">
                <p className="font-semibold text-slate-900">{t.name}</p>
                <p className="text-slate-500 mt-1">{t.description}</p>
                <p className="text-slate-400 mt-1">{t.var_count} body variable{t.var_count === 1 ? "" : "s"} · {t.language}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="panel p-5" data-testid="notifications-toggles">
        <p className="overline text-[#EA580C] mb-4">Event toggles</p>
        <div className="divide-y divide-slate-100">
          {(data.events || []).map((ev) => (
            <div key={ev.key} className="flex items-start justify-between gap-4 py-3">
              <div>
                <p className="text-sm font-semibold text-slate-900">{ev.label}</p>
                <p className="text-xs text-slate-500 mt-0.5">
                  Template <code>{ev.template}</code> · Module {ev.module}
                </p>
              </div>
              <button onClick={() => toggle(ev)} disabled={!ev.can_toggle || saving === ev.key}
                data-testid={`notif-toggle-${ev.key}`}
                className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
                  ev.enabled ? "bg-emerald-500" : "bg-slate-300"} ${!ev.can_toggle ? "opacity-50 cursor-not-allowed" : ""}`}>
                <span className={`inline-block h-4 w-4 rounded-full bg-white transform transition-transform ${
                  ev.enabled ? "translate-x-6" : "translate-x-1"}`} />
              </button>
            </div>
          ))}
        </div>
        {(data.events || []).length === 0 && (
          <p className="py-3 text-sm text-slate-500" data-testid="notifications-empty-state">
            No active Interakt templates are configured for your enabled modules.
          </p>
        )}
        {!canManage && (
          <p className="mt-4 text-xs text-slate-500">
            Some toggles are read-only for your role. Ask your business or module admin to change them.
          </p>
        )}
      </div>

      {!wa.configured && (
        <div className="panel p-5 border-amber-200 bg-amber-50 text-amber-900 text-sm">
          <p className="font-semibold">Set up Interakt to start sending</p>
          <ol className="mt-2 list-decimal pl-5 space-y-1">
            <li>Add <code>WHATSAPP_API_TOKEN</code> (Interakt secret API key) to backend/.env.</li>
            <li>Optionally set <code>WHATSAPP_API_URL</code> — defaults to Interakt public endpoint.</li>
            <li>Set <code>WHATSAPP_WEBHOOK_SECRET</code> and point Interakt webhook to <code>/api/whatsapp/webhook</code> with the same secret in the <code>X-Webhook-Secret</code> header (or <code>?secret=</code> query).</li>
            <li>Restart the backend so the environment is reloaded.</li>
          </ol>
        </div>
      )}
    </div>
  );
}


function ModuleGstSettings({ business, canManage, onSaved }) {
  const [rates, setRates] = useState({});
  const workspaceModules = business?.modules;
  const diagnosticSetting = business?.diagnostic_enabled;
  const available = useMemo(() => {
    const modules = workspaceModules || [];
    const diagnosticEnabled = modules.includes("lab") && diagnosticSetting !== false;
    return [
      modules.includes("hospital") && ["hospital", "Hospital"],
      modules.includes("pharmacy") && ["pharmacy", "Pharmacy"],
      modules.includes("lab") && ["laboratory", "Laboratory"],
      diagnosticEnabled && ["diagnostic", "Diagnostic / Scanning"],
    ].filter(Boolean);
  }, [workspaceModules, diagnosticSetting]);

  useEffect(() => {
    const moduleGst = business?.module_gst || {};
    setRates(Object.fromEntries(available.map(([key]) => [
      key,
      String(moduleGst[key]?.percent ?? 12),
    ])));
  }, [business, available]);

  const saveRate = async (module, label) => {
    try {
      await api.put("/settings/module-gst", { module, percent: Number(rates[module]) });
      toast.success(`${label} GST saved.`);
      onSaved();
    } catch (error) {
      toast.error(apiError(error));
    }
  };

  return (
    <section className="panel mt-6 p-6 md:p-8" data-testid="module-gst-settings-panel">
      <div className="mb-5">
        <p className="overline text-[#EA580C]">Module GST defaults</p>
        <p className="mt-2 text-sm text-slate-600">
          Each enabled billing workspace keeps an independent default for newly created invoices.
        </p>
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        {available.map(([module, label]) => (
          <div key={module} className="rounded-lg border border-slate-200 p-4">
            <label className="block text-sm font-semibold text-slate-900" htmlFor={`gst-${module}`}>
              {label} GST %
            </label>
            <div className="mt-3 flex flex-wrap items-center gap-3">
              <input
                id={`gst-${module}`}
                type="number"
                min="0"
                max="100"
                step="0.01"
                className={`${inputCls} !w-32`}
                data-testid={`module-gst-${module}-input`}
                value={rates[module] ?? ""}
                disabled={!canManage}
                onChange={(event) => setRates({ ...rates, [module]: event.target.value })}
              />
              {canManage && (
                <button
                  type="button"
                  className={ghostBtnCls}
                  data-testid={`module-gst-${module}-save-button`}
                  onClick={() => saveRate(module, label)}
                >
                  Save
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
